import GeneralHelper from '../../00_utilities/helpers/GeneralHelper';
import BreakpointHelper from '../../00_utilities/helpers/BreakpointHelper';

/**
 * Main navigation component for the site
 * Handles responsive navigation behavior, dropdowns, and accessibility
 *
 * @class MainNavigation
 * @example
 * // HTML structure
 * // <div class="navigation" data-component="molecules/MainNavigation">...</div>
 *
 * // JavaScript initialization via ComponentHelper
 * const navigation = document.querySelector('[data-component="molecules/MainNavigation"]');
 * const mainNav = new MainNavigation(navigation);
 * mainNav.init();
 */
class MainNavigation {
    /**
     * Creates an instance of MainNavigation
     *
     * @param {HTMLElement} elem - The main navigation container element
     */
    constructor(elem) {
        this.elem = elem;
        this.breakpoint = parseInt(getComputedStyle(this.elem).getPropertyValue('--main-navigation-breakpoint'), 10);
        this.layoutDesktop = BreakpointHelper.aboveBreakpoint(this.breakpoint);
        this.mainToggle = document.getElementById('main-menu-toggle');
    }

    /**
     * Initialize the navigation component
     * Sets up event handlers and configures responsive behavior
     *
     * @example
     * // Initialize after DOM is ready
     * document.addEventListener('DOMContentLoaded', () => {
     *   const mainNav = new MainNavigation(document.querySelector('.navigation'));
     *   mainNav.init();
     * });
     */
    init() {
        if (this.layoutDesktop) {
            this.getSubnavigationHeight();
        }
        this.setAccessibilityLabels();
        this.behaviourOnChange();
        this.initResizeSensibleFunctions();
    }

    /**
     * Prevents scrolling on the body element
     * Used when navigation is open on mobile
     *
     * @static
     * @example
     * // Prevent scrolling when mobile menu is open
     * MainNavigation.preventScrolling();
     */
    static preventScrolling() {
        window.requestAnimationFrame(() => {
            document.body.classList.add('no-scroll');
        });
    }

    /**
     * Allows scrolling on the body element
     * Used when navigation is closed on mobile
     *
     * @static
     * @example
     * // Allow scrolling when mobile menu is closed
     * MainNavigation.allowScrolling();
     */
    static allowScrolling() {
        window.requestAnimationFrame(() => {
            document.body.classList.remove('no-scroll');
        });
    }

    /**
     * Helper function to handle checkbox state changes consistently
     *
     * @static
     * @param {HTMLInputElement} checkbox - The checkbox element to modify
     * @param {boolean} check - Whether to check or uncheck the box
     * @param {boolean} triggerChange - Whether to trigger the change event
     *
     * @example
     * // Check a checkbox and trigger its change event
     * const checkbox = document.querySelector('#menu-toggle');
     * MainNavigation.checkboxHelper(checkbox, true, true);
     */
    static checkboxHelper(checkbox, check, triggerChange) {
        if (typeof checkbox.prop === 'undefined') {
            checkbox.checked = check;

            if (triggerChange) {
                const event = new Event('change');
                checkbox.dispatchEvent(event);
            }
            return;
        }
        checkbox
            .prop('checked', check)
            .attr('checked', check);
        if (triggerChange) {
            checkbox.trigger('change');
        }
    }

    /**
     * Closes all navigation items and resets navigation state
     *
     * @static
     * @example
     * // Close all navigation items
     * MainNavigation.closeNavigation();
     */
    static closeNavigation() {
        const mainToggle = document.getElementById('main-menu-toggle');
        MainNavigation.checkboxHelper(mainToggle, false, true);

        const navigationToggleCheckboxes = document.querySelectorAll('.page-header .navigation [type="checkbox"]');
        for (const checkbox of navigationToggleCheckboxes) {
            MainNavigation.checkboxHelper(checkbox, false, true);
        }
    }

    /**
     * Event listener for clicks outside the navigation to close it
     *
     * @param {MouseEvent} e - The click event
     */
    bodyClickListener(e) {
        const { target } = e;
        const isNavigation = (target.closest('.navigation__item') !== null
                || document.querySelector('.navigation__item') === target
                || document.querySelector('.menu-toggle-input') === target);

        if (!isNavigation) {
            MainNavigation.closeNavigation();
            MainNavigation.allowScrolling();
        }
    }

    /**
     * Closes all child menu items of a given parent
     *
     * @param {HTMLElement} current - The current element being processed
     */
    closeChildren(current) {
        let currentMenu = this.elem;

        if (current.id !== 'main-menu-toggle') {
            currentMenu = current.parentNode;
        }

        const menuChildren = currentMenu.querySelectorAll('.navigation__item--has-children');
        for (const sibling of menuChildren) {
            const siblingsToggle = sibling.querySelector('.navigation__menu-toggle-input');
            if (siblingsToggle) {
                MainNavigation.checkboxHelper(siblingsToggle, false);
            }
        }
    }

    /**
     * Returns all siblings of an element
     *
     * @param {HTMLElement} element - The element to find siblings for
     * @returns {Array<HTMLElement>} - Array of sibling elements
     */
    getSiblings(element) {
        return Array.prototype.filter.call(element.parentNode.children, (sibling) => {
            return sibling !== element;
        });
    }

    /**
     * Closes all sibling menu items of a given element
     *
     * @param {HTMLElement} current - The current element being processed
     */
    closeSiblings(current) {
        let currentMenu = this.elem;

        if (current.id !== 'main-menu-toggle') {
            currentMenu = current;

            if (current.type === 'checkbox') {
                currentMenu = current.parentNode;
            }
        }

        const menuSiblings = this.getSiblings(currentMenu);

        for (const sibling of menuSiblings) {
            const siblingsToggle = sibling.querySelector('.navigation__menu-toggle-input');
            if (siblingsToggle) {
                MainNavigation.checkboxHelper(siblingsToggle, false);
            }
        }
    }

    /**
     * Sets accessibility attributes for navigation elements
     *
     * @param {string} pressed - The aria-pressed attribute value ('true' or 'false')
     */
    setAccessibilityLabels(pressed = 'false') {
        if (this.layoutDesktop) {
            this.mainToggle.removeAttribute('aria-pressed');

            return;
        }

        this.mainToggle.setAttribute('aria-pressed', pressed);
    }

    /**
     * Sets up event handlers for navigation state changes
     * Prevents or allows scrolling when mobile navigation is open
     */
    behaviourOnChange() {
        const mainToggleHandling = (event) => {
            this.setAccessibilityLabels(this.mainToggle.checked);

            if (this.mainToggle.checked) {
                MainNavigation.preventScrolling();
                if (this.layoutDesktop) {
                    document.addEventListener('click', this.bodyClickListener);
                }
                return;
            }

            this.closeChildren(event.currentTarget);
            MainNavigation.allowScrolling();
            if (this.layoutDesktop) {
                document.removeEventListener('click', this.bodyClickListener);
            }
        };

        this.mainToggle.addEventListener('change', mainToggleHandling, false);

        const toggleInputs = this.elem.querySelectorAll('.navigation__menu-toggle-input');
        for (const toggle of toggleInputs) {
            toggle.addEventListener('change', (event) => {
                const { checked } = event.currentTarget;

                if (event.currentTarget.parentNode.classList.contains('navigation__item--main') && this.layoutDesktop) {
                    MainNavigation.checkboxHelper(this.mainToggle, checked, true);

                    MainNavigation.allowScrolling();
                    if (checked) {
                        if (!this.layoutDesktop) {
                            MainNavigation.preventScrolling();
                        }
                    }
                }

                if (checked) {
                    this.closeSiblings(event.currentTarget);
                    return;
                }

                this.closeChildren(event.currentTarget);
            });
        }
    }

    /**
     * Calculates and sets height for subnavigation containers
     * Used for animation and layout purposes
     */
    getSubnavigationHeight() {
        const subNavigationContainer = document.getElementsByClassName('navigation__sub-container');
        for (let i = 0; i < subNavigationContainer.length; i += 1) {
            const subNavigationContainerHeight = subNavigationContainer[i].offsetHeight + 40;
            subNavigationContainer[i].parentElement
                .style.setProperty('--navigation-sub-max-block-size', `${subNavigationContainerHeight}px`);
        }
    }

    /**
     * Initializes resize-sensitive functions and handles breakpoint changes
     */
    initResizeSensibleFunctions() {
        let aboveBreakpoint = BreakpointHelper.aboveBreakpoint(this.breakpoint);
        window.addEventListener('resize', GeneralHelper.throttle(() => {
            this.getSubnavigationHeight();
            const breakpointInfo = BreakpointHelper.breakpointCrossed(
                this.breakpoint,
                aboveBreakpoint,
            );
            aboveBreakpoint = breakpointInfo.aboveBreakpoint;
            const { breakpointCrossed } = breakpointInfo;

            if (breakpointCrossed) {
                this.layoutDesktop = aboveBreakpoint;
                MainNavigation.closeNavigation();
                MainNavigation.allowScrolling();
                this.setAccessibilityLabels();
            }
        }, 200));
    }
}

export default MainNavigation;
